// ABOUTME: Builds the employer's shortlist CSV from what the employer card shows, and decides who is offered it.
// ABOUTME: Pure functions; ExportShortlistCsv renders the button and saves the file.
import ActiveValues from '$constants/ActiveValues';
import { CandidateData } from '$types/candidates';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import computeYearsOfExperience from '$utils/computeYearsOfExperience';

export interface ShortlistCsvContext {
  position: PositionInternalDto;
  /** The position's fee, as the employer card adds it to the salary */
  fee: number;
  currencyCode: string;
  usdToCurrency: (amount: number) => number;
  /** Left out on the public share page, where full profiles sit behind sign-in; the column goes with it */
  profileUrl?: (candidateId: number) => string;
}

/** Staff turn the export on per company in the back office; it's off unless they do. */
export const canExportShortlist = (
  position: PositionInternalDto | undefined,
  cps: ActiveValues
): boolean =>
  cps === ActiveValues.ShortList &&
  position?.Company?.ShortlistCsvExport === true;

// Excel needs it to read accented names as UTF-8; written as a code so formatters can't hide it.
export const BOM = String.fromCharCode(0xfeff);

// A spreadsheet runs a cell starting with one of these as a formula; names and titles come from candidates.
const FORMULA_START = /^[=+\-@\t\r]/;

export const csvCell = (value: string | number | null | undefined): string => {
  const text = value === null || value === undefined ? '' : String(value);
  const safe =
    typeof value === 'string' && FORMULA_START.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
};

const shortlistedOn = (candidate: CandidateData): string => {
  const created = candidate.CandidatesPositionStatus?.[0]?.CreateTime;
  return created ? new Date(created).toISOString().slice(0, 10) : '';
};

/** The same columns as SPKEmployerPositionCandidateCard: never the email or the salary without the fee. */
export const buildShortlistCsv = (
  candidates: CandidateData[],
  {
    position,
    fee,
    currencyCode,
    usdToCurrency,
    profileUrl,
  }: ShortlistCsvContext
): string => {
  const header = [
    'Candidate ID',
    'First name',
    'Last name',
    ...(profileUrl ? ['Profile'] : []),
    'Latest job title',
    'Latest company',
    'Country',
    'Years of experience',
    `Monthly salary (${currencyCode})`,
    'Demand score',
    'Shortlisted on',
  ];
  const rows = candidates.map((candidate) => {
    const latestJob = candidate.CandidateWorkExperience?.[0];
    const score = position.CandidatesPositionStatus?.find(
      (status) => status.Candidate?.Id === candidate.Id
    )?.Candidate?.Score;
    return [
      candidate.Id,
      candidate.User?.FirstName,
      candidate.User?.LastName,
      ...(profileUrl ? [profileUrl(candidate.Id)] : []),
      latestJob?.JobTitle,
      latestJob?.Company,
      candidate.ResidenceCountry,
      computeYearsOfExperience(candidate.CandidateWorkExperience),
      usdToCurrency(
        (candidate.CandidateJobInterest?.SalaryLowerBound || 0) + fee
      ),
      score,
      shortlistedOn(candidate),
    ];
  });
  return `${BOM}${[header, ...rows]
    .map((row) => row.map(csvCell).join(','))
    .join('\r\n')}`;
};
